import type { ComboboxItem } from "@mantine/core";
import { randomId } from "@mantine/hooks";
import * as XLSX from "xlsx";

import { ensureItemIds } from "./CheckList.utils";
import { CHECKLIST_IMPORT_TEMPLATE as TEMPLATE } from "./CheckListImportTemplate.config";
import { CheckListType, type ICheckListForm } from "./CheckLists.types";

export interface ImportError {
  row?: number;
  field: string;
  message: string;
}

const cell = (rows: any[][], row: number, col: number): string =>
  String(rows[row]?.[col] ?? "").trim();

// Matched by label text, not fixed row/column position, so this survives rows shifting.
const normalizeLabel = (value: string): string =>
  value
    .trim()
    .replace(/\s*\*\s*$/, "")
    .toLowerCase();

const findLabelRow = (rows: any[][], label: string, col: number): number =>
  rows.findIndex(
    row => normalizeLabel(String(row?.[col] ?? "")) === normalizeLabel(label),
  );

const buildLookup = <T>(options: Record<string, T>) => {
  const map = new Map(
    Object.entries(options).map(([label, value]) => [
      label.trim().toLowerCase(),
      value,
    ]),
  );
  return (raw: string): T | undefined => map.get(raw.trim().toLowerCase());
};

const resolveLogbookId = (
  name: string,
  logbooks: ComboboxItem[],
): string | undefined =>
  logbooks.find(
    lb => (lb.label ?? "").trim().toLowerCase() === name.trim().toLowerCase(),
  )?.value;

export const parseCheckListWorkbook = async (
  file: File,
  logbooks: ComboboxItem[],
): Promise<{ form: ICheckListForm | null; errors: ImportError[] }> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName =
    workbook.SheetNames.find(
      n => n.toLowerCase() === TEMPLATE.sheetName.toLowerCase(),
    ) ?? workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  if (!sheet) {
    return {
      form: null,
      errors: [
        {
          field: "file",
          message: `Could not find an "${TEMPLATE.sheetName}" sheet in this file.`,
        },
      ],
    };
  }

  // sheet_to_json anchors row 0 to the sheet's declared used range (which starts
  // partway down this template), so force it to start at A1 for accurate row numbers.
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");
  range.s.r = 0;
  range.s.c = 0;
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    blankrows: true,
    range,
  });

  const errors: ImportError[] = [];
  const { labels } = TEMPLATE;
  const resolveType = buildLookup(TEMPLATE.checklistTypes);
  const resolveNa = buildLookup(TEMPLATE.naOptions);
  const resolveUnit = buildLookup(TEMPLATE.units);

  // Checklist Name / Description may have the value in column B of the
  // label's row, or column A of the row below - accept either.
  const readLabelValue = (labelRow: number): string => {
    if (labelRow < 0) return "";
    return cell(rows, labelRow, 1) || cell(rows, labelRow + 1, 0);
  };

  const nameLabelRow = findLabelRow(rows, labels.checklistName, 0);
  const name = readLabelValue(nameLabelRow);
  if (nameLabelRow < 0 || !name) {
    errors.push({ field: "name", message: "Checklist Name is required." });
  }

  const descLabelRow = findLabelRow(rows, labels.description, 0);
  const desc = readLabelValue(descLabelRow);

  const typeLabelRow = findLabelRow(rows, labels.checklistType, 0);
  const typeLabel = typeLabelRow >= 0 ? cell(rows, typeLabelRow, 1) : "";
  const type = resolveType(typeLabel);
  if (!type) {
    errors.push({
      field: "type",
      message: `Checklist Type must be one of ${Object.keys(TEMPLATE.checklistTypes).join(", ")}.`,
    });
  }

  const naLabelRow = findLabelRow(rows, labels.na, 0);
  const naLabel = naLabelRow >= 0 ? cell(rows, naLabelRow, 1) : "";
  const naFlag = resolveNa(naLabel);
  if (naFlag === undefined) {
    errors.push({
      field: "na",
      message: `N/A must be one of ${Object.keys(TEMPLATE.naOptions).join(", ")}.`,
    });
  }

  const logbooksLabelRow = findLabelRow(rows, labels.logbooks, 0);
  const logbookName =
    logbooksLabelRow >= 0 ? cell(rows, logbooksLabelRow, 1) : "";
  let lbid: string | undefined;
  if (!logbookName) {
    errors.push({ field: "logbooks", message: "Logbooks is required." });
  } else {
    lbid = resolveLogbookId(logbookName, logbooks);
    if (!lbid) {
      errors.push({
        field: "logbooks",
        message: `Logbook "${logbookName}" does not match any logbook on this vessel.`,
      });
    }
  }

  // Items are read for as long as column A holds the template's pre-numbered sequence.
  const tableHeaderRow = rows.findIndex(
    row =>
      normalizeLabel(String(row?.[1] ?? "")) === normalizeLabel(labels.tableName) &&
      normalizeLabel(String(row?.[2] ?? "")) === normalizeLabel(labels.tableUnit),
  );

  const list: any[] = [];
  if (tableHeaderRow < 0) {
    errors.push({
      field: "items",
      message: `Could not find the completion points table (a "${labels.tableName}" / "${labels.tableUnit}" header row).`,
    });
  } else {
    for (let r = tableHeaderRow + 1; r < rows.length; r++) {
      const rowNumberCell = cell(rows, r, 0);
      if (!rowNumberCell || Number.isNaN(Number(rowNumberCell))) break;

      const itemName = cell(rows, r, 1);
      const unitLabel = cell(rows, r, 2);
      const unit = resolveUnit(unitLabel);

      if (!itemName && !unit) continue;

      const rowNumber = r + 1;
      if (!itemName) {
        errors.push({
          row: rowNumber,
          field: "name",
          message: `Row ${rowNumber}: Name is required when a Unit is selected.`,
        });
        continue;
      }
      if (!unit) {
        errors.push({
          row: rowNumber,
          field: "unit",
          message: `Row ${rowNumber}: Unit must be one of ${Object.keys(TEMPLATE.units).join(", ")}.`,
        });
        continue;
      }

      list.push({
        id: randomId(),
        text: itemName,
        unit,
        // The sheet has one checklist-wide N/A flag; apply it to every item that supports N/A.
        naAvailable: naFlag && unit !== "header" && unit !== "multiple",
        fields: unit === "multiple" ? [] : undefined,
      });
    }
  }

  if (list.length === 0 && tableHeaderRow >= 0 && errors.length === 0) {
    errors.push({
      field: "items",
      message: "Add at least one completion point.",
    });
  }

  if (errors.length > 0) {
    return { form: null, errors };
  }

  const form: ICheckListForm = {
    name,
    desc,
    lbid: [lbid as string],
    type: type as CheckListType,
    sort: 0,
    list: ensureItemIds(list),
  };

  return { form, errors: [] };
};
